import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  TrackByFunction,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatCheckbox } from '@angular/material/checkbox';
import {} from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { distinctUntilChanged, takeUntil } from 'rxjs';
import { DynamicOptionsService } from './services/dynamic-options.service';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FormlyErrorComponent } from '../formly-error.component';
import { MatHint } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { debugLog } from 'app/shared/utils/debug-logger';

/** Storage shape for the field value. Default is 'array' (string[]). */
export type MulticheckboxValueShape = 'array' | 'objectMap';

@Component({
  selector: 'formly-field-custom-multicheckbox',
  template: `
    <div [formGroup]="subform" class="checkbox-group" [class.hidden]="isFieldHidden()">
      @if (props.label) {
        <!--
          [innerHTML] ipv interpolation zodat admins via form-builder een <a>-tag
          (bv. naar algemene voorwaarden) in het label kunnen zetten. Angular's
          DomSanitizer strips XSS automatisch; <a href target>-tags blijven staan.
        -->
        <label class="font-medium flex justify-between mb-2">
          <span [innerHTML]="props.label"></span>{{ field.props.required ? ' *' : '' }}
          <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          @if (isLoading()) {
            <mat-spinner diameter="24"> </mat-spinner>
          }
        </label>
      }
      @if (availableOptions()?.length > 0) {
        @for (option of availableOptions(); track trackByOption($index, option)) {
          <div>
            @if (option.value && subform.controls[getControlName(option)]) {
              <mat-checkbox [formlyDataCy]="field" [formControlName]="getControlName(option)">
                <span [innerHTML]="option.label"></span>
              </mat-checkbox>
            }
          </div>
        }
      } @else if (isLoading()) {
        <p class="mb-4 text-accent"><i>Laden van options..</i></p>
      } @else {
        <p class="mb-4 text-accent"><i>Geen options beschikbaar</i></p>
      }
    </div>
    @if (field.props.description) {
      <mat-hint class="inline-block mb-2"><span [innerHTML]="field.props.description"></span></mat-hint>
    }
    <app-formly-error [field]="field"></app-formly-error>
  `,
  styles: `
    :host .checkbox-group {
      margin-top: 8px;
      ::ng-deep mat-checkbox {
        label {
          line-height: 1.25;
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCheckbox,
    FormlyModule,
    ReactiveFormsModule,
    InfoTooltipComponent,
    FormlyErrorComponent,
    MatHint,
    MatProgressSpinner,
    FormlyDataCyDirective,
  ],
})
export class CustomMulticheckboxTypeComponent extends FieldType implements OnInit, OnDestroy {
  private destroy$ = injectDestroy();
  private dynamicOptionsService = inject(DynamicOptionsService);
  private fb = inject(FormBuilder);

  availableOptions = signal<Array<{ label: string; value: any }>>([]);
  linkedData = signal([]);
  optionsBasedOnOtherField = computed(() => {
    return this.field.props?.optionsArray?.some(optionsItem => {
      return optionsItem.filters?.some(optionsFilter => optionsFilter.basedOn === 'dynamic');
    });
  });
  private dynamicFilterValue = signal(undefined);
  isFieldHidden = computed(
    () => this.props.hideIfOptionsEmpty && (this.availableOptions().length === 0 || this.dynamicFilterValue() == null)
  );
  isLoading = signal(true);
  loaded = signal(false);

  // Form group for individual checkboxes
  subform: FormGroup = this.fb.group({});

  // TrackBy function for better performance
  trackByOption: TrackByFunction<any> = (index: number, option: any) => {
    // Mirror getControlName() — never return the raw value object (would
    // coerce to "[object Object]" and collapse every row into one).
    const v = option.value;
    if (v && typeof v === 'object') {
      return v.id || v.slug || v.name || option.label || index;
    }
    return v ?? option.label ?? index;
  };

  // Cleanup function for dynamic options subscriptions
  private cleanup?: () => void;

  /**
   * Returns true when the field should use object-map storage {key: bool}.
   *
   * Default (valueShape unset or 'objectMap') → objectMap, for backwards
   * compatibility with all existing consumers (hideUserRole, disableUserRole,
   * bagVerifyShowUserRole, etc.) that rely on object-map storage.
   *
   * Set valueShape: 'array' to opt in to string[] storage.
   */
  private get isObjectMapMode(): boolean {
    return (this.props['valueShape'] as MulticheckboxValueShape | undefined) !== 'array';
  }

  /**
   * Derive the checked state for a single option key from the current
   * formControl value, respecting the configured valueShape.
   */
  isChecked(optionKey: string): boolean {
    const v = this.field.formControl.value;
    if (this.isObjectMapMode) {
      return !!v?.[optionKey];
    }
    // array mode (default)
    return Array.isArray(v) && v.includes(optionKey);
  }

  /**
   * Write a toggled state back to the formControl.
   *
   * In objectMap mode, ALL known option-keys are written with their boolean
   * state so that a key that was never toggled has an explicit `false`.
   * This is required for the `istrue` visibility operator to return false
   * instead of undefined for unchecked keys.
   *
   * Stale keys that exist in the stored value but are NOT in knownOptions
   * are preserved so that data is not lost if an option is temporarily
   * removed from the taxonomy.
   */
  toggle(optionKey: string, checked: boolean, knownOptions: string[]): void {
    if (this.isObjectMapMode) {
      const current: Record<string, boolean> = this.field.formControl.value ?? {};
      const next: Record<string, boolean> = { ...current };
      // Ensure all known option-keys are present with a default of false
      knownOptions.forEach(k => {
        if (!(k in next)) {
          next[k] = false;
        }
      });
      next[optionKey] = checked;
      debugLog('Multicheckbox', 'toggle objectMap', { optionKey, checked, next });
      this.field.formControl.markAsDirty();
      this.field.formControl.setValue(next);
    } else {
      // array mode
      const current: string[] = Array.isArray(this.field.formControl.value) ? [...this.field.formControl.value] : [];
      if (checked) {
        if (!current.includes(optionKey)) {
          current.push(optionKey);
        }
      } else {
        const idx = current.indexOf(optionKey);
        if (idx !== -1) {
          current.splice(idx, 1);
        }
      }
      debugLog('Multicheckbox', 'toggle array', { optionKey, checked, next: current });
      this.field.formControl.markAsDirty();
      this.field.formControl.setValue(current);
    }
  }

  ngOnInit() {
    if (!this.optionsBasedOnOtherField()) {
      this.initializeOptions();
    }
    this.initializeDynamicOptions();
    this.setupValueChanges();

    // Initialize the formControl value based on the configured valueShape
    if (!this.field.formControl.value) {
      if (this.isObjectMapMode) {
        this.field.formControl.setValue({});
      } else {
        this.field.formControl.setValue([]);
      }
    }
  }

  private initializeOptions(): void {
    this.dynamicOptionsService.initializeOptions(this.field).subscribe(options => {
      this.availableOptions.set(options);
      this.updateFormControls(options);
      this.isLoading.set(false);
      this.loaded.set(true);
    });
  }

  ngOnDestroy() {
    this.cleanup?.();
  }

  private initializeDynamicOptions(): void {
    if (!this.optionsBasedOnOtherField()) {
      return;
    }

    const { linkedData, cleanup } = this.dynamicOptionsService.initializeDynamicOptions(
      {
        field: this.field,
        onOptionsUpdate: options => {
          this.availableOptions.set(options);
          this.updateFormControls(options);
        },
        onFilterValueUpdate: value => {
          this.dynamicFilterValue.set(value);
        },
      },
      this.destroy$
    );

    this.linkedData.set(linkedData);
    this.cleanup = cleanup;
  }

  private setupValueChanges(): void {
    // Handle form control changes
    this.field.formControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(value => {
      // Update subform controls with main form control values
      Object.keys(this.subform.controls).forEach(key => {
        const currentValue = this.isObjectMapMode
          ? (value?.[key] ?? false)
          : (Array.isArray(value) ? value.includes(key) : false);
        if (this.subform.controls[key].value !== currentValue) {
          this.subform.controls[key].patchValue(currentValue, { emitEvent: false });
        }
      });

      // Fetch new options when the filter value changes
      if (this.dynamicFilterValue()) {
        this.isLoading.set(true);
        this.dynamicOptionsService
          .fetchOptionsForValue(this.dynamicFilterValue(), this.linkedData(), '', this.field)
          .subscribe(options => {
            this.isLoading.set(false);
            this.availableOptions.set(options);
            this.updateFormControls(options);
            this.validateRequired();
          });
      }
    });
  }

  validateRequired() {
    if (this.props.required) {
      const fv = this.field.formControl.value;
      const hasValue = this.isObjectMapMode
        ? Object.values(fv ?? {}).some(v => v)
        : Array.isArray(fv) && fv.length > 0;
      this.field.formControl.setErrors(hasValue ? null : { required: true });
    }
  }

  private updateFormControls(options: Array<{ label: string; value: any }>): void {
    this.field.formControl.markAsTouched();
    // Remove controls that are no longer in options
    Object.keys(this.subform.controls).forEach(key => {
      if (!options.some(option => this.getControlName(option) === key)) {
        this.subform.removeControl(key);
      }
    });

    // Add or update controls for current options
    options.forEach(option => {
      const controlName = this.getControlName(option);
      // Derive initial checked state depending on the configured storage shape
      const currentValue = this.isObjectMapMode
        ? (this.field.formControl.value?.[controlName] ?? false)
        : (Array.isArray(this.field.formControl.value) ? this.field.formControl.value.includes(controlName) : false);

      if (!this.subform.contains(controlName)) {
        const control = this.fb.control({
          value: currentValue,
          disabled: this.props.disabled,
        });

        this.subform.addControl(controlName, control);

        // Subscribe to individual checkbox changes and delegate to toggle()
        control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(checked => {
          const knownOptions = options.map(o => this.getControlName(o));
          this.toggle(controlName, checked as boolean, knownOptions);
        });
      }
    });
  }

  getControlName(option: { label: string; value: any }): string {
    const v = option.value;
    // Object-shaped value (taxonomy entry, role, entry-small, …):
    //   prefer id, then fall through to slug/name/label so we NEVER coerce the
    //   whole object into a control name. A returned object stringifies to
    //   "[object Object]" — every option then shares one FormControl, all
    //   checkboxes bind to the same value, and toggling one toggles all.
    //   This was the root cause of the bedrijven required-documents inversion
    //   bug: documenten.hoofdtype taxonomy entries had id:null (V19.7
    //   backfill gap) so id was falsy and the original `id ? id : value`
    //   fallback returned the entry object, collapsing every key.
    if (v && typeof v === 'object') {
      return v.id || v.slug || v.name || option.label;
    }
    return v;
  }
}
